import { Box, Typography, useTheme } from '@mui/material';
import { useMemo } from 'react';
import { sanitizeHtml } from '@/utils/sanitizeHtml';

interface SafeHtmlRendererProps {
    htmlContent: string;
    variant?: any;
    style?: React.CSSProperties;
    className?: string;
}

function SafeHtmlRenderer({ htmlContent, variant = 'paragraph4regular', style, className }: SafeHtmlRendererProps) {
    const theme = useTheme();

    const sanitizedHtml = useMemo(() => sanitizeHtml(htmlContent), [htmlContent]);

    const containsHtml = /<[^>]*>/g.test(htmlContent);

    if (!containsHtml) {
        return (
            <Typography variant={variant} style={{ whiteSpace: 'pre-wrap', ...style }} className={className}>
                {htmlContent}
            </Typography>
        );
    }

    const typographyStyles = theme.typography[variant] || theme.typography.paragraph4regular;

    return (
        <Box
            component="div"
            className={className}
            sx={{
                ...typographyStyles,
                '& img': {
                    maxWidth: '100%',
                    height: 'auto',
                    display: 'block',
                    margin: '8px 0',
                },
                '& p': {
                    margin: '8px 0',
                    ...typographyStyles,
                },
                '& div': {
                    ...typographyStyles,
                },
                '& span': {
                    ...typographyStyles,
                },
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                    margin: '16px 0 8px 0',
                    ...typographyStyles,
                    fontWeight: 'bold',
                },
                '& a': {
                    ...typographyStyles,
                    color: 'primary.main',
                    textDecoration: 'underline',
                },
                '& strong, & b': {
                    ...typographyStyles,
                    fontWeight: 'bold',
                },
                '& em, & i': {
                    ...typographyStyles,
                    fontStyle: 'italic',
                },
                '& ul, & ol': {
                    paddingLeft: '20px',
                    margin: '8px 0',
                    ...typographyStyles,
                },
                '& li': {
                    margin: '4px 0',
                    ...typographyStyles,
                },
                ...style,
            }}
            dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
        />
    );
}

export default SafeHtmlRenderer;
